import React, { useState, useEffect } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { AddProductToChargeDetails } from 'lane-shared/domains/billingPayments/types';
import { getPricing } from 'lane-shared/domains/productCatalog/helpers';
import { SupportedLocaleEnum } from 'localization';
import {
  type SupportedPaymentCurrency,
  formatCurrency,
} from 'domains-billing-currency';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';

import useUserLocale from 'hooks/useUserLocale';

import styles from './addProductToChargeStyles.scss';
import { priceInputToComponents } from 'lane-shared/domains/billingPayments/helpers';

export function AddedProductsTotal({
  listOfProducts,
  currency,
}: {
  listOfProducts: AddProductToChargeDetails[];
  currency: SupportedPaymentCurrency;
}) {
  const { t } = useTranslation();
  const isAdminFeeEnabled = useBpAdminFeeChargeCodeEnabled();
  const [summary, setSummary] = useState({
    subTotal: '',
    adminFeeTax: '',
    tax: '',
    total: '',
  });

  const twoDecimalsWithCurrency = formatCurrency(currency, {
    minimumFractionDigits: 2,
  });

  const locale = useUserLocale();

  useEffect(() => {
    const { subTotal, adminFeeTax, tax, total } = getAmounts(
      listOfProducts,
      currency,
      locale,
      isAdminFeeEnabled
    );

    setSummary({
      subTotal: twoDecimalsWithCurrency(subTotal),
      adminFeeTax: twoDecimalsWithCurrency(adminFeeTax),
      tax: twoDecimalsWithCurrency(tax),
      total: twoDecimalsWithCurrency(total),
    });
  }, [listOfProducts, isAdminFeeEnabled]);

  return (
    <div className={styles.addedProductsSummary}>
      <div className={styles.summaryRow}>
        <div className={styles.subTotalLabel}>
          {t('abp.charges.addProduct.summary.subtotal')}
        </div>
        <div
          className={styles.subTotalValue}
          id="create-charge-summary-subtotal"
        >
          {summary.subTotal}
        </div>
      </div>
      {isAdminFeeEnabled && (
        <div className={styles.summaryRow}>
          <div className={styles.taxLabel}>
            {t('abp.charges.addProduct.summary.adminFeeTax')}
          </div>
          <div
            className={styles.taxValue}
            id="create-charge-summary-admin-fee-tax"
          >
            {summary.adminFeeTax}
          </div>
        </div>
      )}
      <div className={styles.summaryRow}>
        <div className={styles.taxLabel}>
          {t('abp.charges.addProduct.summary.tax')}
        </div>
        <div className={styles.taxValue} id="create-charge-summary-tax">
          {summary.tax}
        </div>
      </div>
      <div className={cx(styles.summaryRow, styles.total)}>
        <div className={styles.totalRowLabel}>
          {t('abp.charges.addProduct.summary.total')}
        </div>
        <div className={styles.totalRowValue} id="create-charge-summary-total">
          {summary.total}
        </div>
      </div>
    </div>
  );
}

export function getAmounts(
  listOfProducts: AddProductToChargeDetails[],
  currency: SupportedPaymentCurrency,
  locale: SupportedLocaleEnum,
  isAdminFeeFeatureEnabled = false
) {
  let subTotal = 0;
  let tax = 0;
  let adminFeeTax = 0;
  let total = 0;

  listOfProducts.forEach(product => {
    const priceComponents = priceInputToComponents(product);

    if (product.sellingPrice) {
      priceComponents.sellingPrice = product.sellingPrice;
    }

    const pricing = getPricing({
      currency,
      locale,
      priceComponents,
      isAdminFeeFeatureEnabled,
    });

    const quantity = Number(product.quantity);

    subTotal += pricing.listPrice * quantity;
    tax += pricing.grossTax * quantity;
    adminFeeTax += pricing.grossAdminFeeTax * quantity;
    total += pricing.netPrice * quantity;
  });

  return { subTotal, tax, adminFeeTax, total };
}
